import React, { useState, useEffect } from 'react';
import { FileText, Image, File, Download, Trash2, ExternalLink, RefreshCw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FILE_ICONS = {
  'application/pdf': { icon: FileText, color: '#EF4444' },
  'image/jpeg': { icon: Image, color: '#10B981' },
  'image/jpg': { icon: Image, color: '#10B981' },
  'image/png': { icon: Image, color: '#10B981' },
  'image/svg+xml': { icon: Image, color: '#10B981' },
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': { icon: FileText, color: '#2563EB' },
  'application/msword': { icon: FileText, color: '#2563EB' },
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': { icon: FileText, color: '#059669' },
  'application/vnd.ms-excel': { icon: FileText, color: '#059669' },
};

const FilesList = ({ clientId, adminKey = null, onFileChange, taskId = null }) => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);

  const isAdmin = !!adminKey;

  const loadFiles = async () => {
    try {
      setLoading(true);
      const headers = {};
      if (adminKey) {
        headers['x-admin-key'] = adminKey;
      }

      const response = await fetch(
        `${BACKEND_URL}/api/clients/${clientId}/files`,
        { headers }
      );

      if (response.ok) {
        const data = await response.json();
        // Filter by taskId if provided
        const filteredFiles = taskId 
          ? data.filter(f => f.task_id === taskId)
          : data;
        setFiles(filteredFiles);
        
        if (onFileChange) {
          onFileChange(filteredFiles);
        }
      } else {
        console.error('Failed to load files');
      }
    } catch (error) {
      console.error('Error loading files:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (clientId) {
      loadFiles();
    }
  }, [clientId, taskId]);

  const handleDelete = async (fileId) => {
    if (!window.confirm('Are you sure you want to delete this file?')) {
      return;
    }

    setDeleting(fileId);
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/clients/${clientId}/files/${fileId}`,
        {
          method: 'DELETE',
          headers: {
            'x-admin-key': adminKey
          }
        }
      );

      if (response.ok) {
        setFiles(files.filter(f => f.id !== fileId));
        if (onFileChange) {
          onFileChange(files.filter(f => f.id !== fileId));
        }
      } else {
        alert('Failed to delete file');
      }
    } catch (error) {
      console.error('Error deleting file:', error);
      alert('Error deleting file');
    } finally {
      setDeleting(null);
    }
  };

  const formatDate = (isoString) => {
    try {
      const date = new Date(isoString);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
      });
    } catch {
      return '';
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getFileIcon = (mimeType) => {
    const config = FILE_ICONS[mimeType] || { icon: File, color: '#6B7280' };
    const IconComponent = config.icon;
    return <IconComponent size={20} color={config.color} />;
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
        <RefreshCw size={24} style={{ animation: 'spin 1s linear infinite' }} />
        <p style={{ marginTop: '8px' }}>Loading files...</p>
      </div>
    );
  }

  if (files.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
        <File size={48} color="#D1D5DB" style={{ margin: '0 auto 16px' }} />
        <p style={{ fontSize: '14px' }}>No files uploaded yet</p>
      </div>
    );
  }

  return (
    <div style={{ width: '100%' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {files.map(file => (
          <div
            key={file.id}
            style={{
              padding: '16px',
              background: '#F9FAFB',
              border: '1px solid #E5E7EB',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '12px'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 0 }}>
              <div style={{ flexShrink: 0 }}>
                {getFileIcon(file.mime_type)}
              </div>
              
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#111827',
                  marginBottom: '4px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  {file.file_name}
                </p>
                <p style={{ fontSize: '12px', color: '#6B7280' }}>
                  {formatFileSize(file.size_bytes)} • Uploaded by {file.uploader_role === 'admin' ? 'BM-Assist' : 'You'}
                  {file.created_at && ` • ${formatDate(file.created_at)}`}
                </p>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
              {file.file_url && (
                <a
                  href={file.file_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    padding: '8px 12px',
                    background: '#4D7CFE',
                    color: '#fff',
                    borderRadius: '6px',
                    fontSize: '13px',
                    fontWeight: '600',
                    textDecoration: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  <Download size={16} />
                  Download
                </a>
              )}
              
              {isAdmin && (
                <button
                  onClick={() => handleDelete(file.id)}
                  disabled={deleting === file.id}
                  style={{
                    padding: '8px 12px',
                    background: deleting === file.id ? '#9CA3AF' : '#EF4444',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: deleting === file.id ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px'
                  }}
                >
                  <Trash2 size={16} />
                  {deleting === file.id ? 'Deleting...' : 'Delete'}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      <button
        onClick={loadFiles}
        style={{
          marginTop: '16px',
          padding: '8px 16px',
          background: '#F3F4F6',
          color: '#374151',
          border: '1px solid #D1D5DB',
          borderRadius: '6px',
          fontSize: '13px',
          fontWeight: '600',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '6px'
        }}
      >
        <RefreshCw size={14} />
        Refresh
      </button>
    </div>
  );
};

export default FilesList;
